/**
 * Names where a nutrition figure came from.
 *
 * The important job is the fourth case. Three of these are the manufacturer's
 * own printed numbers for this exact product; the fourth is a reference value
 * for the FOOD, correct for a raw vegetable but not the pack's own label. It
 * gets a different colour, a different word and its own explanatory line so it
 * cannot be mistaken for the others at a glance.
 */

export type SourceKind =
  | 'retailer_label'
  | 'barcode_exact'
  | 'name_matched'
  | 'generic_reference';

const LABEL: Record<SourceKind, string> = {
  retailer_label: 'Product label',
  barcode_exact: 'Barcode match',
  name_matched: 'Matched product',
  generic_reference: 'Generic estimate',
};

const DETAIL: Record<SourceKind, string> = {
  retailer_label: 'Taken from the nutrition panel published for this exact product.',
  barcode_exact: 'Matched to this product’s barcode in a nutrition database.',
  name_matched: 'Matched by brand, product name and pack size — not by barcode.',
  generic_reference:
    'A reference value for this type of food, not this product. Whole foods carry no nutrition panel, so no product-specific figures exist.',
};

export function SourceBadge({ kind }: { kind: SourceKind }): React.JSX.Element {
  const generic = kind === 'generic_reference';
  return (
    <span
      className={`src-badge${generic ? ' src-generic' : ''}`}
      title={DETAIL[kind]}
      aria-label={`${LABEL[kind]}. ${DETAIL[kind]}`}
    >
      {generic ? '≈' : '✓'} {LABEL[kind]}
    </span>
  );
}

/** The full sentence, for the panel under the nutrition table. */
export function sourceDetail(kind: SourceKind): string {
  return DETAIL[kind];
}

